import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { buildSeo, humanizeSlug } from "@/lib/seo";
import { Layout } from "@/components/Layout";
import { RouteNotFound } from "@/components/RouteNotFound";
import { GameResultDateView } from "@/components/GameResultDateView";
import { getResultsForGame, isSaneResultDate, getSafeTodayIso } from "@/data/results";
import { drawNavigation, drawSocialMeta, findRouteGame, resultFaqs } from "@/lib/results-nav";
import { breadcrumbSchema, faqPageSchema } from "@/lib/jsonld";
import { canonicalUrl, isArchivedResultPath } from "@/lib/seo-policy";
import { gameOgImage } from "@/lib/og-images";
import { formatDate } from "@/lib/format";
import { gamePaths } from "@/lib/game-routing";

export const Route = createFileRoute("/games/$gameSlug/results/$date")({
  loader: ({ params }) => {
    const game = findRouteGame(params.gameSlug);
    if (!game || !isSaneResultDate(params.date) || params.date > getSafeTodayIso()) {
      throw notFound();
    }
    const draw = game
      ? getResultsForGame(game.id, 200).find((result) => result.draw_date === params.date)
      : undefined;
    if (!draw) {
      // Valid past date, but outside the archive we hold: send crawlers to the
      // game's history page instead of serving an empty (soft 404) result page.
      throw redirect({
        to: "/games/$gameSlug/history",
        params: { gameSlug: params.gameSlug },
        statusCode: 301,
        replace: true,
      });
    }
    const nav = drawNavigation(game.id, params.date);
    return {
      date: params.date,
      gameName: game.name,
      drawSchedule: game.draw_schedule ?? null,
      numbers: draw.winning_numbers,
      bonus: draw.bonus_numbers,
      multiplier: draw.multiplier,
      prev: nav.prev,
      next: nav.next,
    };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return {
      meta: [
        { title: "Lottery result unavailable — USA Lottery Club" },
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
    const name = loaderData.gameName || humanizeSlug(params.gameSlug);
    const paths = gamePaths(params.gameSlug);
    const path = paths.result(params.date);
    const dateLabel = formatDate(params.date);
    const numbers = loaderData.numbers ?? [];
    const bonus = loaderData.bonus ?? [];
    const numbersText = numbers.join(", ");
    const seo = buildSeo({
      title: `${name} Numbers ${params.date} — Winning Results`,
      description: `${name} winning numbers for ${dateLabel}: ${numbersText || "see the full result"}. See bonus numbers, multiplier, past draws, and the full ${name} results archive.`,
      path,
      type: "article",
      image: gameOgImage(params.gameSlug),
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: `${name} Winning Numbers — ${dateLabel}`,
          datePublished: params.date,
          dateModified: params.date,
          mainEntityOfPage: path,
          about: { "@type": "Game", name: `${name} Lottery` },
        },
        breadcrumbSchema([
          { name: "Home", item: "/" },
          { name, item: paths.game },
          { name: "Results history", item: paths.history },
          { name: dateLabel, item: path },
        ]),
        faqPageSchema(
          resultFaqs({
            gameName: name,
            dateLabel,
            drawSchedule: loaderData.drawSchedule ?? null,
            numbers,
            bonus,
            multiplier: loaderData.multiplier,
          }),
        ),
      ],
    });
    return {
      ...seo,
      meta: [
        ...seo.meta,
        // Old draws stay crawlable (follow) but out of the index so crawl
        // budget goes to the game, history and recent-result pages.
        ...(isArchivedResultPath(path) ? [{ name: "robots", content: "noindex, follow" }] : []),
        ...drawSocialMeta({
          scope: name,
          date: params.date,
          dateLabel,
          numbers,
          bonus,
          multiplier: loaderData.multiplier,
        }),
      ],
      links: [
        ...seo.links,
        ...(loaderData.prev ? [{ rel: "prev", href: canonicalUrl(paths.result(loaderData.prev)) }] : []),
        ...(loaderData.next ? [{ rel: "next", href: canonicalUrl(paths.result(loaderData.next)) }] : []),
      ],
    };
  },
  component: GameResultByDatePage,
  notFoundComponent: GameResultNotFound,
  errorComponent: GameResultNotFound,
});

function GameResultNotFound() {
  const { gameSlug } = Route.useParams();
  return (
    <Layout>
      <RouteNotFound gameSlug={gameSlug} />
    </Layout>
  );
}

function GameResultByDatePage() {
  const { gameSlug, date } = Route.useParams();
  return <GameResultDateView gameSlug={gameSlug} date={date} />;
}
